import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useApp, api } from "@/context/AppContext";
import { toast } from "sonner";
import { Upload, Download, Share2, RefreshCw, ShoppingBag } from "lucide-react";

export default function TryOn() {
  const { id } = useParams();
  const { lang, t } = useApp();
  const head = lang === "hi" ? "font-hi" : "font-display";

  const [data, setData] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploadingPath, setUploadingPath] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [resultUrl, setResultUrl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/sarees/${id}`).then((r) => setData(r.data));
  }, [id]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setUploadingPath(null);
    setResultUrl(null);
    setError(null);
  };

  const generate = async () => {
    if (!file) { toast.error("Choose a photo first"); return; }
    setGenerating(true);
    setError(null);
    setResultUrl(null);
    try {
      let path = uploadingPath;
      if (!path) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/uploads/image?folder=humans", fd, { headers: { "Content-Type": "multipart/form-data" } });
        path = up.data.path;
        setUploadingPath(path);
      }
      const r = await api.post("/tryon", { saree_id: id, human_image_path: path });
      setResultUrl(r.data.result_image_url);
    } catch (err) {
      setError(err?.response?.data?.detail || t.tryon.errorBody);
    } finally {
      setGenerating(false);
    }
  };

  const downloadResult = async () => {
    if (!resultUrl) return;
    try {
      const r = await fetch(resultUrl);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${data.saree.name.replace(/\s+/g, "_")}_tryon.jpg`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      window.open(resultUrl, "_blank");
    }
  };

  const share = async () => {
    if (!resultUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: data.saree.name, text: `Look at me in ${data.saree.name}!`, url: resultUrl });
      } catch { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(resultUrl);
      toast.success("Link copied");
    }
  };

  const buyNow = () => {
    const wa = (data?.store?.whatsapp_number || "").replace(/[^\d]/g, "");
    if (!wa) { toast.error(t.tryon.noWa); return; }
    // Fire-and-forget conversion track
    api.post("/track/buy-click", { saree_id: id }).catch(() => {});
    const msg = t.tryon.waMsg(data.saree.name, data.store.store_name);
    window.open(`https://wa.me/${wa}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const buyShopify = () => {
    if (!data?.saree?.shopify_url) return;
    api.post("/track/buy-click", { saree_id: id }).catch(() => {});
    window.open(data.saree.shopify_url, "_blank");
  };

  if (!data) return <div className="max-w-7xl mx-auto px-4 py-24 text-center text-[#635057]">{t.common.loading}</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 lg:py-14" data-testid="tryon-page">
      <Link to={`/store/${data.store?.store_slug}`} className="text-xs tracking-[0.25em] uppercase text-[#635057] hover:text-[#6B1F3A]" data-testid="tryon-back-link">
        ← {t.common.back}
      </Link>

      <div className="mt-4 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase font-semibold text-[#C9A84C]">✦ {t.tryon.title}</div>
          <h1 className={`${head} text-3xl sm:text-5xl text-[#2D1820] font-light tracking-tight mt-2`} data-testid="tryon-saree-name">
            {data.saree.name}
          </h1>
          <div className="text-[#6B1F3A] font-semibold text-lg mt-1">₹ {Number(data.saree.price).toLocaleString("en-IN")}</div>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
        {/* Saree */}
        <div className="lg:col-span-4">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#635057] font-semibold mb-3">Saree</div>
          <div className="border border-[#E8DFDC] bg-[#FCFAF8] aspect-[3/4] overflow-hidden">
            <img src={data.saree.image_url} alt={data.saree.name} className="w-full h-full object-cover" />
          </div>
          {data.saree.description && <p className="text-sm text-[#635057] mt-4 leading-relaxed">{data.saree.description}</p>}
        </div>

        {/* Upload */}
        <div className="lg:col-span-4">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#635057] font-semibold mb-3">{t.tryon.step1}</div>
          <label className="border-2 border-dashed border-[#E8DFDC] hover:border-[#C9A84C] cursor-pointer block aspect-[3/4] bg-[#FCFAF8] overflow-hidden" data-testid="human-photo-dropzone">
            {preview ? (
              <img src={preview} alt="you" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-[#635057] p-4 text-center">
                <Upload size={28} />
                <div className="text-sm font-medium text-[#2D1820] mt-3">{t.tryon.upload}</div>
                <div className="text-xs mt-2">{t.tryon.step1Hint}</div>
              </div>
            )}
            <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="human-photo-input" />
          </label>
          {preview && (
            <label className="mt-3 inline-block text-xs tracking-[0.2em] uppercase text-[#6B1F3A] cursor-pointer hover:text-[#C9A84C]">
              <input type="file" accept="image/*" className="hidden" onChange={onFile} />
              ↻ {t.tryon.change}
            </label>
          )}
          <button onClick={generate} disabled={!file || generating} className="btn-primary w-full mt-5" data-testid="generate-tryon-btn">
            {generating ? t.tryon.generating : t.tryon.generate}
          </button>
        </div>

        {/* Result */}
        <div className="lg:col-span-4">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#635057] font-semibold mb-3">{t.tryon.step3}</div>
          <div className="border border-[#E8DFDC] bg-[#FCFAF8] aspect-[3/4] overflow-hidden relative flex items-center justify-center" data-testid="tryon-result-box">
            {generating && (
              <div className="flex flex-col items-center text-center px-4">
                <div className="relative">
                  <div className="text-6xl tryon-loader" aria-hidden>🥻</div>
                  <div className="tryon-ring" />
                </div>
                <div className={`${head} mt-6 text-lg text-[#6B1F3A] italic`}>{t.tryon.generating}</div>
                <div className="text-xs text-[#635057] mt-2">~10–20 sec</div>
              </div>
            )}
            {!generating && resultUrl && (
              <img src={resultUrl} alt="try-on result" className="w-full h-full object-cover" data-testid="tryon-result-image" />
            )}
            {!generating && !resultUrl && !error && (
              <div className="text-center text-[#635057] text-sm px-4">
                <div className="text-4xl mb-2 opacity-50" aria-hidden>✨</div>
                Your AI try-on will appear here
              </div>
            )}
            {!generating && error && (
              <div className="text-center text-[#9B2226] p-6" data-testid="tryon-error">
                <div className="font-medium">{t.tryon.errorTitle}</div>
                <div className="text-xs mt-2 text-[#635057]">{error}</div>
              </div>
            )}
          </div>

          {resultUrl && !generating && (
            <div className="mt-5 space-y-3" data-testid="tryon-actions">
              {data?.saree?.shopify_url && (
                <button
                  onClick={buyShopify}
                  className="btn-primary w-full inline-flex items-center justify-center gap-2 !text-sm !tracking-[0.18em]"
                  data-testid="buy-shopify-btn"
                >
                  <ShoppingBag size={16} /> {t.tryon.buyShopify}
                </button>
              )}
              <button onClick={buyNow} className="btn-whatsapp w-full" data-testid="buy-whatsapp-btn">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M20.5 3.5A11.9 11.9 0 0 0 12 0 12 12 0 0 0 1.6 18l-1.6 6 6.2-1.6A12 12 0 1 0 20.5 3.5Zm-8.5 18a9.9 9.9 0 0 1-5.1-1.4l-.4-.2-3.7 1 1-3.6-.2-.4A10 10 0 1 1 12 21.5Zm5.5-7.4-2-1c-.3-.1-.5-.2-.7.1l-.9 1.1c-.2.2-.3.2-.6.1a8.1 8.1 0 0 1-2.4-1.5 9 9 0 0 1-1.6-2c-.2-.3 0-.5.1-.6l.5-.6.3-.5c.1-.2 0-.3 0-.5l-.9-2.1c-.3-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4a3 3 0 0 0-1 2.2c0 1.3 1 2.6 1.1 2.8.1.2 2 3 4.7 4.2 1.7.7 2.3.8 3.1.7.5-.1 1.6-.7 1.8-1.3.2-.6.2-1.2.2-1.3-.1-.1-.3-.2-.6-.3Z"/></svg>
                {t.tryon.buyNow}
              </button>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={downloadResult} className="btn-secondary inline-flex items-center justify-center gap-2 !px-4 !py-3 text-[11px]" data-testid="download-btn">
                  <Download size={14} /> {t.tryon.download}
                </button>
                <button onClick={share} className="btn-secondary inline-flex items-center justify-center gap-2 !px-4 !py-3 text-[11px]" data-testid="share-btn">
                  <Share2 size={14} /> {t.tryon.share}
                </button>
              </div>
              <button onClick={() => { setResultUrl(null); }} className="text-xs tracking-[0.2em] uppercase text-[#635057] hover:text-[#6B1F3A] inline-flex items-center gap-2 mt-1">
                <RefreshCw size={12} /> {t.tryon.retry}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
